"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
import { MessageCircle, CheckSquare, ClipboardList, BookOpen, MessageSquare, PenTool, TrendingUp, Award } from "lucide-react";
export default function HowOurServicesWorkTogether() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step1Ref,
    isVisible: step1Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step2Ref,
    isVisible: step2Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step3Ref,
    isVisible: step3Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step4Ref,
    isVisible: step4Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step5Ref,
    isVisible: step5Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step6Ref,
    isVisible: step6Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step7Ref,
    isVisible: step7Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: step8Ref,
    isVisible: step8Visible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const steps = [{
    icon: MessageCircle,
    title: "Initial Enquiry or Consultation",
    description: "We begin with a free consultation to understand your child's needs, goals, and challenges. This allows us to recommend the most suitable programme and ensure we're the right fit for your family.",
    visible: step1Visible,
    ref: step1Ref,
    delay: "100ms"
  }, {
    icon: CheckSquare,
    title: "Programme Selection",
    description: "Based on our consultation, you'll choose the programme that best fits your child's requirements.",
    visible: step2Visible,
    ref: step2Ref,
    delay: "200ms"
  }, {
    icon: ClipboardList,
    title: "Baseline Assessment",
    description: "Before lessons begin, we conduct a comprehensive baseline assessment to identify your child's current level, strengths, and areas for improvement. This ensures our teaching is precisely targeted.",
    visible: step3Visible,
    ref: step3Ref,
    delay: "300ms"
  }, {
    icon: BookOpen,
    title: "Lessons Begin",
    description: "Your child starts regular online lessons with their dedicated tutor, combining academic teaching in Science, Maths, or English with confidence coaching and study skills development.",
    visible: step4Visible,
    ref: step4Ref,
    delay: "400ms"
  }, {
    icon: MessageSquare,
    title: "Lesson Feedback to Parents",
    description: "After every session, you receive detailed feedback on what was covered, how your child performed, and areas of focus for upcoming lessons. This keeps you informed and engaged in their progress.",
    visible: step5Visible,
    ref: step5Ref,
    delay: "500ms"
  }, {
    icon: PenTool,
    title: "Weekly Homework & Practice",
    description: "Students receive carefully designed homework and practice tasks to reinforce learning between sessions. This consistent practice is essential for building confidence and mastering key concepts.",
    visible: step6Visible,
    ref: step6Ref,
    delay: "600ms"
  }, {
    icon: TrendingUp,
    title: "Ongoing Progress Updates",
    description: "We provide regular progress reports tracking your child's development across academic performance, confidence levels, and study habits. You'll see measurable improvements throughout the programme.",
    visible: step7Visible,
    ref: step7Ref,
    delay: "700ms"
  }, {
    icon: Award,
    title: "Three Month Progress Assessment",
    description: "At the three-month milestone, we conduct a comprehensive assessment to evaluate overall progress, celebrate achievements, and refine our approach for continued success moving forward.",
    visible: step8Visible,
    ref: step8Ref,
    delay: "800ms"
  }];
  return <section className="relative bg-dark-background py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-16 lg:gap-20">
          {}
          <div ref={leftRef} className="space-y-6 max-w-4xl mx-auto text-center transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(-2rem)"
        }}>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight">How Our Approach Works in Practice</h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Our <strong>premium</strong> offering is built on a <strong>carefully structured service</strong> that goes beyond the traditional high street tutoring model. Our approach delivers <strong>thorough</strong> and <strong>intentional</strong> support at every stage. We prioritise <strong>clarity</strong>, <strong>confidence</strong>, and <strong>consistency</strong> through a <strong>close partnership with parents</strong>, ensuring that each step creates lasting impact and meaningful progress for your child.
            </p>
          </div>

          {}
          <div className="relative max-w-4xl mx-auto w-full">
            {}
            <div className="absolute left-6 top-12 bottom-12 w-px bg-secondary/30 hidden lg:block" />

            {}
            <div className="space-y-8">
              {steps.map((step, i) => {
              const Icon = step.icon;
              return <div key={i} data-index={i} ref={step.ref} className="relative transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)]" style={{
                opacity: step.visible ? 1 : 0,
                transform: step.visible ? "translateX(0)" : "translateX(2rem)",
                transitionDelay: step.delay
              }}>
                    <Card className="relative bg-dark-background border border-secondary/20 shadow-medium hover:shadow-strong hover:border-secondary/40 transition-all duration-[var(--duration-medium)]">
                      <div className="flex gap-6 p-6">
                        {}
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center border border-secondary/30">
                            <Icon className="w-6 h-6 text-secondary" />
                          </div>
                        </div>

                        {}
                        <div className="space-y-2">
                          <h3 className="font-display text-xl font-semibold text-white">
                            {step.title}
                          </h3>
                          <p className="text-white/70 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>

                      {}
                      <div className="absolute -left-3 top-6 w-6 h-6 rounded-full bg-secondary text-foreground-secondary flex items-center justify-center text-sm font-bold shadow-medium">
                        {i + 1}
                      </div>
                    </Card>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}